<template>
  <Screen
    v-if="isUserCreatorInGroup"
    back-button
    back-button-text="Project"
    :back-button-route="`/projects/${projectId}/boards`"
    :header-title="headerTitle"
    left-bigger
    left-auto-height
    center-transparent
    :user="user"
    :group-id="selectedGroupId"
    class="feedback"
  >
    <aside
      v-if="hasStyles"
      slot="screen-left"
      ref="stylesContainer"
      v-infinite-scroll="handleScrollHitBottom"
      class="feedback__sidebar"
      :infinite-scroll-distance="200"
      :infinite-scroll-disabled="hasReachedStylesPaginatedEnd"
    >
      <div
        v-for="style in stylesInfo"
        :key="style.id"
        :class="[
          style.id === activeStyleId ? 'feedback__sidebar-item--active' : '',
          'feedback__sidebar-item'
        ]"
        @click="showStyleFeedback(style.id)"
      >
        <img
          v-if="style.mainOptionURL"
          crossorigin="use-credentials"
          :src="style.mainOptionURL"
          alt="preview"
        >
        <div class="feedback__sidebar-item-info">
          <h4>{{ style.name }}</h4>
          <p>
            {{ style.comments.length }} Comments, {{ style.ratings.length }}
            Ratings
          </p>
        </div>
      </div>
      <div
        v-if="!hasReachedStylesPaginatedEnd"
        :class="[
          'styles__loader',
          { 'styles__loader--visible': isLoadingStyles }
        ]"
      >
        <div
          v-loading="true"
          element-loading-background="transparent"
        />
      </div>
    </aside>
    <aside
      v-if="!isLoadingStyles && !hasStyles"
      slot="screen-left"
      class="feedback__sidebar feedback__sidebar--empty"
    >
      <div>No style has been rated yet.</div>
    </aside>
    <aside
      v-if="isLoadingStyles && !hasStyles"
      slot="screen-left"
      class="feedback__sidebar feedback__sidebar--loading"
    >
      <div>Loading Styles...</div>
    </aside>
    <section
      v-if="activeStyleId"
      slot="screen-center"
      class="feedback__main"
    >
      <header class="feedback__main-header">
        <div class="feedback__main-header-head">
          <div class="feedback__main-header-head-text">
            <h2>{{ stylesInfo[activeStyleId].name }}</h2>
          </div>
          <div class="feedback__main-header-ratings">
            <ElButton
              v-if="hasStyles"
              type="primary"
              :disabled="isDownloadingFeedbackCSV"
              @click="downloadFeedbackCSV"
            >
              Download CSV
            </ElButton>
          </div>
        </div>
        <div
          v-if="stylesInfo[activeStyleId].ratingElementLength"
          class="feedback__rating-overview"
        >
          <div
            v-for="ratingType in ratingValues"
            :key="ratingValues[ratingType]"
            :class="[
              'feedback__rating',
              {
                'feedback__rating--green': ratingType === ratingValues.STRONG
              },
              {
                'feedback__rating--yellow': ratingType === ratingValues.OK
              },
              {
                'feedback__rating--orange': ratingType === ratingValues.WEAK
              },
              {
                'feedback__rating--red': ratingType === ratingValues.NO
              }
            ]"
            :style="{
              width: `${stylesInfo[activeStyleId].ratingElementLength[ratingType]}%`
            }"
          >
            <p>{{ stylesInfo[activeStyleId].ratingOverview[ratingType] }}</p>
          </div>
        </div>
        <div
          v-if="stylesInfo[activeStyleId].notes"
          class="feedback__presenter-notes"
        >
          <h2>Presenter Notes</h2>
          <p>{{ stylesInfo[activeStyleId].notes }}</p>
        </div>
      </header>
      <div
        v-if="stylesInfo[activeStyleId].feedback.length > 0"
        class="feedback__comments"
      >
        <div
          v-for="feedback in stylesInfo[activeStyleId].feedback"
          :key="feedback.id"
          class="feedback__single-comment"
        >
          <div class="feedback__comment-username">
            {{ feedback.user.email }}
          </div>
          <div class="feedback__comment-last-updated">
            {{ parseDate(feedback.updated_at) }}
          </div>
          <div class="feedback__comment-rating">
            <div
              :class="[
                'feedback__comment-rating-box',
                {
                  'feedback__rating--grey': feedback.rating === null
                },
                {
                  'feedback__rating--green':
                    feedback.rating === ratingValues.STRONG
                },
                {
                  'feedback__rating--yellow':
                    feedback.rating === ratingValues.OK
                },
                {
                  'feedback__rating--orange':
                    feedback.rating === ratingValues.WEAK
                },
                {
                  'feedback__rating--red': feedback.rating === ratingValues.NO
                }
              ]"
            >
              <p v-if="feedback.rating === null">
                No Rating
              </p>
              <p v-else>
                {{ ratingOptions[feedback.rating].label }}
              </p>
            </div>
          </div>
          <div class="feedback__comment-content">
            <p>{{ feedback.comment }}</p>
          </div>
        </div>
      </div>
      <div
        v-else
        class="feedback__empty"
      >
        No feedback yet
      </div>
    </section>
  </Screen>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'
import { S3Trim } from '@/mixins/s3Trim'
import { imageResolutions } from '@/constants/image'
import { ratingValues, ratingOptions } from '@/constants/rating'
import Utils from '@/services/utils'
import api from '@/services/api'

import Screen from '@/components/Screen.vue'
import debounce from 'debounce'

const DATE_PARSING_OPTIONS = {
  day: 'numeric',
  year: 'numeric',
  month: 'long'
}

export default {
  name: 'ProjectFeedback',

  components: {
    Screen
  },

  mixins: [Workspace, Projects, S3Trim],

  data: () => ({
    ratingValues,
    ratingOptions,
    isLoadingStyles: true,
    isDownloadingFeedbackCSV: false,
    stylesInfo: {},
    activeStyleId: null
  }),

  computed: {
    ...mapGetters([
      'getStyles',
      'getHasReachedStylesPaginatedEnd',
      'getStylePreviewOption'
    ]),

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    },

    /**
     * @returns {boolean}
     */
    hasStyles () {
      return this.styles.length > 0
    },

    /**
     * @returns {Array}
     */
    styles () {
      return this.getStyles
    },

    /**
     * @returns {boolean}
     */
    hasReachedStylesPaginatedEnd () {
      return this.getHasReachedStylesPaginatedEnd
    }
  },

  created () {
    this.resetStyles()
  },

  methods: {
    ...mapActions([
      'fetchStylesPaginated',
      'incrementStylesPaginatedPage',
      'resetStylesPaginated'
    ]),

    /**
     * @param {number} styleId
     */
    showStyleFeedback (styleId) {
      this.activeStyleId = styleId
    },

    /**
     * @param   {string} dateString
     *
     * @returns {string}
     */
    parseDate (dateString) {
      return new Date(dateString).toLocaleDateString(
        'en-GB',
        DATE_PARSING_OPTIONS
      )
    },

    /**
     */
    async fetchStyles () {
      const payload = {
        projectId: this.projectId
      }

      this.isLoadingStyles = true

      if (this.$refs.stylesContainer) {
        this.$refs.stylesContainer.dispatchEvent(new CustomEvent('scroll'))
      }

      if (!this.hasReachedStylesPaginatedEnd) {
        await this.fetchStylesPaginated(payload)
      }

      this.stylesInfo = this.computeFeedback(this.styles)

      if (this.styles.length > 0 && !this.activeStyleId) {
        this.activeStyleId = this.styles[0].id
      }

      this.isLoadingStyles = false
    },

    /**
     * @param   {object[]} styles
     *
     * @returns {object}
     */
    computeFeedback (styles) {
      const stylesInfo = {}

      // Format Data
      styles.forEach(style => {
        const styleInfo = {
          id: style.id,
          name: style.name,
          notes: style.notes
        }
        let styleInfoTotalRatings = 0

        const stylePreviewOption = this.getStylePreviewOption(style.id)

        if (stylePreviewOption) {
          styleInfo.mainOptionURL = this.s3Trim(
            stylePreviewOption.assets_file,
            imageResolutions.STYLE_FEEDBACK
          )
        } else {
          styleInfo.mainOptionURL = require('../../assets/images/shirt-placeholder.png')
        }

        styleInfo.feedback = style.feedback.filter(
          singleFeedback =>
            singleFeedback.rating !== null || singleFeedback.comment !== null
        )
        styleInfo.ratings = styleInfo.feedback.filter(
          singleFeedback => singleFeedback.rating !== null
        )
        styleInfo.comments = styleInfo.feedback.filter(
          singleFeedback => singleFeedback.comment !== null
        )
        styleInfoTotalRatings = styleInfo.ratings.length

        if (styleInfoTotalRatings > 0) {
          const ratingVolume = this.getRatingVolume(styleInfo.ratings)

          styleInfo.ratingElementLength = {}
          styleInfo.ratingOverview = {}

          Object.values(ratingValues).forEach(value => {
            const ratingCount = ratingVolume[value]

            styleInfo.ratingElementLength[value] = Math.round(
              (ratingCount / styleInfoTotalRatings) * 100
            )

            const ratingOverview = `${ratingCount} / ${styleInfoTotalRatings}`

            styleInfo.ratingOverview[value] = ratingOverview
          })
        }

        stylesInfo[style.id] = styleInfo
      })

      return stylesInfo
    },

    /**
     * @param   {object[]} ratings
     *
     * @returns {object}
     */
    getRatingVolume (ratings) {
      const ratingVolume = {}

      Object.values(ratingValues).forEach(ratingType => {
        ratingVolume[ratingType] = 0
      })

      ratings.forEach(value => {
        ratingVolume[value.rating]++
      })

      return ratingVolume
    },

    /**
     */
    handleScrollHitBottom: debounce(
      /**
       *
       */
      function () {
        this.incrementStylesPaginatedPage()

        this.fetchStyles()
      }, 200),

    /**
     */
    async resetStyles () {
      this.resetStylesPaginated()

      if (this.$refs.stylesContainer) {
        this.$refs.stylesContainer.scrollTop = 0
      }

      await this.fetchStyles()
    },

    /**
     */
    async downloadFeedbackCSV () {
      this.isDownloadingFeedbackCSV = true

      await api.downloadProjectFeedbackCSV(this.projectId)

      this.isDownloadingFeedbackCSV = false
    }
  }
}
</script>

<style lang="scss" scoped>
$min-height-sidebar: spacing(15);
$height-comment-list: calc(70% - #{$gutter});
$height-loader: spacing(6);
$width-comment-username: 20%;
$width-comment-text: 40%;
$border-radius-default-half: $border-radius-default / 2;

p {
  margin: 0;
}

// //////////////////////////////////////////
// Feedback Main CSS
// //////////////////////////////////////////
.feedback__main {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

// Feedback Main header
.feedback__main-header {
  width: 100%;
  padding: $gutter;
  background: $white;
  border-radius: $border-radius-default;
}

.feedback__main-header-ratings {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
}

.feedback__main-header-head {
  display: flex;
  justify-content: space-between;
  width: 100%;

  h2 {
    font-size: $spacer-unit-x4;
  }

  h3 {
    font-size: $spacer-unit-x2;
  }
}

.feedback__main-header-head-text {
  display: flex;
  align-items: flex-end;

  h2 {
    font-size: $spacer-unit-x4;
  }

  h3 {
    margin-bottom: $spacer-unit-small;
    margin-left: $spacer-unit;
    color: $grey;
    font-size: $spacer-unit-x2;
    opacity: 0.7;
  }
}

// Color for ratings
.feedback__rating {
  display: flex;
  align-items: center;
  height: 100%;
  overflow: hidden;

  p {
    width: 100%;
    text-align: center;
  }

  &.feedback__rating--grey {
    color: $grey;
    background: $grey-opacity-40;
  }

  &.feedback__rating--green {
    color: $green;
    background: $green-opacity-40;
  }

  &.feedback__rating--yellow {
    color: $yellow;
    background: $yellow-opacity-40;
  }

  &.feedback__rating--orange {
    color: $orange;
    background: $orange-opacity-40;
  }

  &.feedback__rating--red {
    color: $red;
    background: $red-opacity-40;
  }
}

.feedback__rating-overview {
  display: flex;
  align-items: center;
  width: 100%;
  height: spacing(3);
  margin-top: $spacer-unit-x2;
  overflow: hidden;
  border-radius: $border-radius-default-half;
}

.feedback__presenter-notes {
  width: 100%;
  margin-top: $spacer-unit-x2;

  h2 {
    color: $grey;
    font-size: spacing(3);
    opacity: 0.7;
  }

  p {
    margin-top: $spacer-unit;
  }
}

// Feedback Main comments section
.feedback__comments {
  @include scroll-y-smooth();

  width: 100%;
  height: $height-comment-list;
  margin-top: $gutter;
}

.feedback__single-comment {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  min-height: $spacer-unit-x4;
  margin-bottom: $spacer-unit-x2;
  padding: $spacer-unit-x2;
  text-align: center;
  background: $white;
  border-radius: $border-radius-default;

  > div {
    flex: 1 1 auto;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .feedback__comment-username {
    width: $width-comment-username;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .feedback__comment-rating-box {
    width: spacing(12);
    margin: auto;
    padding: $spacer-unit;
    font-size: spacing(1.5);
    border-radius: $border-radius-default-half;
  }

  .feedback__comment-content {
    flex: 3 1 auto;
    width: $width-comment-text;

    p {
      width: 100%;
    }
  }
}

.feedback__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

// //////////////////////////////////////////
// Feedback Sidebar
// //////////////////////////////////////////

.feedback__sidebar {
  @include scroll-y-smooth();

  width: 100%;
  height: 100%;
  min-height: $min-height-sidebar;
}

.feedback__sidebar--empty,
.feedback__sidebar--loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback__sidebar-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: spacing(9);
  margin-bottom: $margin-small;
  padding: $spacer-unit;
  object-fit: contain;
  background: $grey-blue-light;
  border: 2px solid transparent;
  border-radius: $border-radius-default;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border: 2px solid $blue;
  }

  &.feedback__sidebar-item--active {
    border: 2px solid $blue;
  }

  img {
    width: spacing(6);
    height: auto;
    max-height: spacing(7);
    margin: auto;
    object-fit: contain;

    @include respond-to('m') {
      margin: 0;
    }
  }

  .feedback__sidebar-item-info {
    display: none;
    margin-left: $spacer-unit-x2;

    @include respond-to('m') {
      display: block;
    }

    h4 {
      @include text-ellipsis(spacing(15));

      margin-bottom: $spacer-unit-small;
    }

    p {
      font-size: $font-size-medium;
    }
  }
}

.styles__loader {
  height: $height-loader;
  opacity: 0;

  > div {
    height: 100%;
  }
}

.styles__loader--visible {
  opacity: 1;
}
</style>
